import { useEffect, useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import type {
    BalanceTimeAuthStatus,
    BalanceTimeData,
} from '../../../../../../common/types/playtime.js'
import { View } from '../../../../../types.js'
import { BalanceTimeView } from '../../../../playtime/balancetime/balancetime.js'

type RangeType = 'week' | 'month' | 'quarter' | 'year'

interface ProfileBalanceTimeTabProps {
    view: View
    employeeId: number
    lang: Language
}

/**
 * The "Tööaeg" tab on another employee's profile. Drives the playtime
 * BalanceTime view from local state (scoped to this one employee via
 * `selected_user`), so it never touches the global /me/balancetime slice. The
 * "whose hours" selector is suppressed (`canSelectUser: false`) — the profile
 * already fixes the subject. Harvest access is the viewing admin's; the server
 * gates `selected_user` and falls back to the admin's token when needed.
 */
export const ProfileBalanceTimeTab = ({ view, employeeId, lang }: ProfileBalanceTimeTabProps) => {
    const [authStatus, setAuthStatus] = useState<BalanceTimeAuthStatus | null>(null)
    const [data, setData] = useState<BalanceTimeData | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [rangeType, setRangeType] = useState<RangeType>('month')
    const [offset, setOffset] = useState(0)

    // Single fetch routine. Explicit overrides win over current filter state so
    // a control can change a value and reload in one go without a stale closure.
    const reload = (override: Record<string, string> = {}) => {
        setIsLoading(true)
        setLoadError(null)
        const params: Record<string, string> = {
            range_type: rangeType,
            offset: String(offset),
            selected_user: String(employeeId),
            ...override,
        }
        void view.api.playtime
            .getBalanceTime(params)
            .then((res) => setData({ ...res.data, canSelectUser: false }))
            .catch(() => setLoadError(t.playtime.balancetime.loadError(lang)))
            .finally(() => setIsLoading(false))
    }

    // Harvest auth status + initial load; re-runs if the target employee changes.
    useEffect(() => {
        let cancelled = false
        setIsLoading(true)
        void view.api.playtime
            .getBalanceTimeAuthStatus()
            .then(async (res) => {
                if (cancelled) return
                setAuthStatus(res.data)
                if (!res.data.isAuthenticated) return
                const d = await view.api.playtime.getBalanceTime({
                    range_type: 'month',
                    offset: '0',
                    selected_user: String(employeeId),
                })
                if (!cancelled) setData({ ...d.data, canSelectUser: false })
            })
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setIsLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [employeeId, view])

    return (
        <BalanceTimeView
            lang={lang}
            embedded
            authStatus={authStatus}
            data={data}
            isLoading={isLoading}
            loadError={loadError}
            rangeType={rangeType}
            selectedUser={String(employeeId)}
            onRangeChange={(r) => {
                setRangeType(r)
                setOffset(0)
                reload({ range_type: r, offset: '0' })
            }}
            onOffsetChange={(delta) => {
                const next = offset + delta
                setOffset(next)
                reload({ offset: String(next) })
            }}
            onOffsetSet={(next) => {
                setOffset(next)
                reload({ offset: String(next) })
            }}
            onUserChange={() => {}}
        />
    )
}
